import { Controller } from '@hotwired/stimulus'
import Swiper from 'swiper'
import { Navigation, Pagination, Thumbs } from 'swiper/modules'
import type { SwiperOptions } from 'swiper/types'

export default class extends Controller {
  static values = {
    slidesPerView: { type: Number, default: 1 },
    options: { type: Object, default: {} },
    deferUntilVisible: { type: Boolean, default: false },
  }
  static targets = ['slider', 'thumbsSlider', 'nextButton', 'prevButton', 'pagination']

  declare readonly sliderTarget: HTMLElement
  declare readonly thumbsSliderTarget: HTMLElement
  declare readonly nextButtonTarget: HTMLElement
  declare readonly prevButtonTarget: HTMLElement
  declare readonly paginationTarget: HTMLElement
  declare readonly slidesPerViewValue: number
  declare readonly optionsValue: SwiperOptions
  declare readonly deferUntilVisibleValue: boolean
  declare hasPaginationTarget: boolean
  declare hasNextButtonTarget: boolean
  declare hasPrevButtonTarget: boolean
  declare hasThumbsSliderTarget: boolean
  declare hasOptionsValue: boolean

  private visibilityObserver?: ResizeObserver

  connect() {
    if (!this.deferUntilVisibleValue) {
      this.initSwiper()
      return
    }

    // NOTE: lazyPreloadPrevNext は初期化時に隣接スライドの loading="lazy" を外すため、
    // display: none のモーダル内で初期化すると開く前に画像が読み込まれる。表示されて幅を持つまで初期化を待つ
    this.visibilityObserver = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.contentRect.width > 0)) {
        this.visibilityObserver?.disconnect()
        // NOTE: Swiper は初期化時に同じ要素を ResizeObserver で監視するため、このコールバック内で初期化すると
        // ブラウザが "ResizeObserver loop completed with undelivered notifications" を出す。次のフレームに回す
        requestAnimationFrame(() => this.initSwiper())
      }
    })
    this.visibilityObserver.observe(this.sliderTarget)
  }

  disconnect() {
    this.visibilityObserver?.disconnect()
  }

  private initSwiper() {
    const defaultOptions: SwiperOptions = {
      modules: [Navigation, Pagination, Thumbs],
      loop: true,
      slidesPerView: this.slidesPerViewValue,
      spaceBetween: 6,
    }

    const additionalOptions: SwiperOptions = this.hasOptionsValue ? this.optionsValue : {}

    const swiperOptions: SwiperOptions = {
      ...defaultOptions,
      ...additionalOptions,
    }

    if (this.hasThumbsSliderTarget) {
      const thumbsSlider = new Swiper(this.thumbsSliderTarget, {
        loop: false,
        slidesPerView: 4,
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
      })
      swiperOptions.thumbs = {
        swiper: thumbsSlider,
      }
    }

    if (this.hasPaginationTarget) {
      swiperOptions.pagination = {
        el: this.paginationTarget,
        clickable: true,
      }
    }

    if (this.hasNextButtonTarget && this.hasPrevButtonTarget) {
      swiperOptions.navigation = {
        nextEl: this.nextButtonTarget,
        prevEl: this.prevButtonTarget,
      }
    }

    new Swiper(this.sliderTarget, swiperOptions)
  }
}
